import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  currencyFormatter,
  IPaymentPlanReport,
  IPaymentPlanReportRow,
  PaymentPlanInstalmentReportStatus,
  SavedFilterType,
  toDecimal,
} from "shared";
import { toast } from "react-toastify";
import {
  ExportCsv,
  FilterPanelTrigger,
  GridColDef,
  GridCsvExportOptions,
  GridFilterListIcon,
  GridFilterModel,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { Download } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";

import { Badge } from "../../../../common/Atoms/Badge";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { MultiOrgSelector } from "../MultiOrgSelector";
import { commonConfig, getTogglableColumns } from "../../../lib/datagrid";
import { ApiSavedFilterPanel } from "../../Common/Datagrid/ApiSavedFilterPanel";
import { SaveFilterButton } from "../../Common/Datagrid/SaveFilterButton";
import { DatagridCard } from "../../Common/Datagrid/DatagridCard";
import { useApplySavedFilterFromUrl } from "../../../hooks/useApplySavedFilterFromUrl";
import { useOrganisationsIncludeDisconnected } from "../../../hooks/useOrganisationsIncludeDisconnected";
import {
  buildOrganisationsFilterColumn,
  getReportFilterValue,
  ReportFilterField,
  reportFilterColumnVisibility,
  reportFilterFieldList,
  withReportFilterValue,
} from "../reportGridFilters";

const statusOptions: { value: PaymentPlanInstalmentReportStatus; label: string; color: `green` | `orange` | `yellow` | `red` | `blue` }[] = [
  { value: `paid`, label: `Paid`, color: `green` },
  { value: `paid_late`, label: `Paid late`, color: `orange` },
  { value: `partially_paid`, label: `Partially paid`, color: `yellow` },
  { value: `overdue`, label: `Overdue`, color: `red` },
  { value: `scheduled`, label: `Scheduled`, color: `blue` },
];

const baseColumns: GridColDef<IPaymentPlanReportRow>[] = [
  {
    field: `organisationName`,
    headerName: `Entity Name`,
    minWidth: 130,
  },
  {
    field: `contactName`,
    headerName: `Contact Name`,
    minWidth: 130,
  },
  {
    field: `invoiceNumber`,
    headerName: `Invoice`,
    valueGetter: (value, row) => row.invoiceNumber || `-`,
    renderCell: ({ row }) => (
      <Link to={ `/${row.organisationId}/invoices/${row.invoiceId}` }>
        <span className={ `text-blue-600 underline whitespace-wrap` }>
          { row.invoiceNumber || `-` }
        </span>
      </Link>
    ),
  },
  {
    field: `instalmentNumber`,
    headerName: `Installment`,
    valueFormatter: (value, row) => `${row.instalmentNumber} of ${row.instalmentCount}`,
    filterable: false,
  },
  {
    field: `dueDate`,
    headerName: `Due Date`,
  },
  {
    field: `amountCents`,
    headerName: `Amount`,
    valueGetter: (value, row) => toDecimal(row.amountCents),
    valueFormatter: (value, row) => currencyFormatter(row.currency)(value),
    filterable: false,
  },
  {
    field: `amountPaidCents`,
    headerName: `Amount Paid`,
    valueGetter: (value, row) => toDecimal(row.amountPaidCents),
    valueFormatter: (value, row) => currencyFormatter(row.currency)(value),
    filterable: false,
  },
  {
    field: `paidDate`,
    headerName: `Paid Date`,
    description: `The date Paidnice picked up the payment from your accounting system`,
    valueGetter: value => value || ``,
  },
  {
    field: `status`,
    headerName: `Status`,
    type: `singleSelect`,
    valueOptions: statusOptions,
    renderCell: ({ value }) => {
      const option = statusOptions.find(o => o.value === value);

      return option ? (
        <Badge
          className={ `max-w-fit` }
          color={ option.color }
          message={ option.label }
          size={ `sm` }
        />
      ) : null;
    },
  },
  {
    field: `planCreatedAt`,
    headerName: `Plan Created`,
  },
  {
    field: `planCreatedBy`,
    headerName: `Created By`,
    minWidth: 180,
  },
];

export function PaymentPlanReport() {
  const apiRequest = useApiRequest();
  const apiRef = useGridApiRef();
  const [reportResponse, setReportResponse] = useState<IPaymentPlanReport>({
    rows: [],
  });
  const [reportLoading, setReportLoading] = useState(false);
  // The org selection lives in the grid filter model so it is captured by
  // saved filter presets
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const { data: allOrganisations } = useOrganisationsIncludeDisconnected();

  const orgOptions = useMemo(() => {
    return allOrganisations.map(org => ({
      value: org.id,
      label: org.legalName,
    }));
  }, [allOrganisations]);

  const selectedOrgs = useMemo(() => {
    const value = getReportFilterValue(filterModel, ReportFilterField.organisations);

    return Array.isArray(value) ? value as string[] : [];
  }, [filterModel]);

  // Serialised so the fetch effect only re-runs when the org selection
  // changes, not on unrelated column filter edits
  const reportQueryKey = JSON.stringify({ orgs: selectedOrgs });

  useEffect(() => {
    const params = JSON.parse(reportQueryKey) as { orgs: string[] };

    async function fetchReport() {
      try {
        setReportLoading(true);

        const response = await apiRequest
          .get<IPaymentPlanReport>(`/reports/paymentplans`, {
            params,
          });

        setReportResponse(response.data);
        setReportLoading(false);
      }
      catch (err) {
        setReportLoading(false);
        console.error(`Error fetching payment plan report`, err);
        toast.error(`Could not fetch payment plan report. ${err?.response?.data?.toString() || ``}`);
      }
    }

    if (params.orgs.length > 0) {
      fetchReport();
    }

    if (params.orgs.length === 0) {
      setReportResponse({
        rows: [],
      });

      setReportLoading(false);
    }
  }, [reportQueryKey, apiRequest]);

  useApplySavedFilterFromUrl(apiRef, SavedFilterType.ReportPaymentPlan, {
    defaultColumnVisibilityModel: reportFilterColumnVisibility,
  });

  function setSelectedOrgs(orgs: string[]) {
    setFilterModel(model => withReportFilterValue(model, ReportFilterField.organisations, `isAnyOf`, orgs));
  }

  // Alternate the background per plan so each invoice's instalments read as a group.
  // Rows arrive ordered by invoice, so the bands follow the default order
  const shadedPlanIds = useMemo(() => {
    const shaded = new Set<number>();
    let shade = true;
    let previousPlanId: number | undefined;

    for (const row of reportResponse.rows) {
      if (row.paymentPlanId !== previousPlanId) {
        shade = !shade;
        previousPlanId = row.paymentPlanId;
      }

      if (shade) {
        shaded.add(row.paymentPlanId);
      }
    }

    return shaded;
  }, [reportResponse.rows]);

  const columns: GridColDef<IPaymentPlanReportRow>[] = useMemo(() => {
    return [
      ...baseColumns,
      // Hidden column carrying the org selection in the filter model
      buildOrganisationsFilterColumn(orgOptions),
    ];
  }, [orgOptions]);

  const viewportHeight = window.innerHeight;
  const tableHeight = viewportHeight
    - 180; // px for the header

  return (
    <div className={ `flex flex-col h-full` }
      style={ { height: tableHeight } }>
      <div className={ `flex gap-4 mb-4 items-end` }>
        <div className={ `w-1/3` }>
          <MultiOrgSelector
            onChange={ setSelectedOrgs }
            selected={ selectedOrgs }
          />
        </div>
      </div>
      <DatagridCard className={ `flex-1` }>
        <DataGridPro
          apiRef={ apiRef }
          columns={ columns }
          rows={ reportResponse.rows }
          loading={ reportLoading }
          getRowId={ row => row.id }
          getRowClassName={ ({ row }) => shadedPlanIds.has(row.paymentPlanId) ? `bg-gray-50` : `` }
          rowHeight={ 38 }
          filterModel={ filterModel }
          onFilterModelChange={ setFilterModel }
          initialState={ {
            columns: {
              columnVisibilityModel: reportFilterColumnVisibility,
            },
          } }
          slots={ { toolbar: CustomToolbar, filterPanel: ApiSavedFilterPanel } }
          showToolbar
          slotProps={ {
            columnsManagement: {
              getTogglableColumns: columns => getTogglableColumns(columns, reportFilterFieldList),
            },
            filterPanel: {
              savedFilterType: SavedFilterType.ReportPaymentPlan,
            },
          } }
          sx={ {
            ...commonConfig.headerStyle,
          } }
        />
      </DatagridCard>
    </div>
  );
}

function CustomToolbar() {
  const csvOptions: GridCsvExportOptions = {
    fileName: `payment_plan_report`,
  };

  return (
    <Toolbar>
      <Tooltip title={ `Filters` }>
        <FilterPanelTrigger render={ <ToolbarButton /> }>
          <GridFilterListIcon fontSize={ `medium` } />
        </FilterPanelTrigger>
      </Tooltip>
      <SaveFilterButton type={ SavedFilterType.ReportPaymentPlan } />
      <div className={ `flex justify-end w-full` }>
        <ExportCsv
          render={ <ToolbarButton /> }
          options={ csvOptions }
        >
          <Download fontSize={ `medium` }/>
        </ExportCsv>
      </div>
    </Toolbar>
  );
}
